import { FeatureFlagName } from '@/types/models';

/**
 * Registry of every per-user feature flag. This is the single source of truth for the
 * flag-management UIs and for server-side name validation.
 *
 * It exists because the flag list used to be hardcoded in two components — the admin
 * manager and the user edit modal — and they drifted: `inbox` was only ever added to the
 * modal, so the manager page silently could not grant it at all. Anything that renders or
 * validates flags reads this list instead.
 *
 * To add a new flag:
 *   1. Add the value to `FeatureFlagName` in `types/models.ts`.
 *   2. Add its entry here, choosing the group carefully (see below).
 *   3. Check it — `hasFeatureFlag`/`isAdminOrFlagged` on the server, `hasFlag` on the client.
 *
 * The two UIs and the `PUT /admin/feature-flags` name check pick it up with no further work.
 */

/**
 * `access` = which areas of the app this person can reach; harmless to hand out, and how
 * a feature gets rolled out gradually.
 *
 * `capability` = an admin-only power granted to a non-admin. These bypass the normal
 * `isAdmin` gate on a write path, so they are the security-relevant ones and the UI
 * separates them for that reason — a reviewer scanning a user's flags should be able to
 * see at a glance whether any of them mean "can act as an admin here".
 */
export type FeatureFlagGroup = 'access' | 'capability';

export interface FeatureFlagDef {
	name: FeatureFlagName;
	label: string;
	description: string;
	group: FeatureFlagGroup;
}

export interface FeatureFlagGroupDef {
	key: FeatureFlagGroup;
	title: string;
	blurb: string;
}

export const FEATURE_FLAG_GROUPS: ReadonlyArray<FeatureFlagGroupDef> = [
	{
		key: 'access',
		title: 'Feature access',
		blurb: 'Which areas of the app this person can reach.',
	},
	{
		key: 'capability',
		title: 'Elevated capabilities',
		blurb: 'Admin-only powers granted to a non-admin. Grant sparingly.',
	},
];

export const FEATURE_FLAGS: ReadonlyArray<FeatureFlagDef> = [
	{
		name: FeatureFlagName.TasksEnabled,
		label: 'Tasks',
		description: 'Access to task management',
		group: 'access',
	},
	{
		name: FeatureFlagName.Accounting,
		label: 'Accounting',
		description: 'Accounts, charges, payments, and accounting reports',
		group: 'access',
	},
	{
		name: FeatureFlagName.Inbox,
		label: 'Inbox',
		description: 'Unified inbox for website forms and homeowner email',
		group: 'access',
	},
	{
		name: FeatureFlagName.SqlAdmin,
		label: 'SQL Admin',
		description: 'Run raw SQL against the database (super admin only)',
		group: 'capability',
	},
	{
		name: FeatureFlagName.EmailTemplatesAdmin,
		label: 'Email Templates',
		description: 'Edit the wording of acknowledgement, information-request, approval and denial letters',
		group: 'capability',
	},
	{
		name: FeatureFlagName.ApplicationRulesAdmin,
		label: 'Application Rules',
		description: 'Edit the shared Exterior Improvement request types and their required-item checklists',
		group: 'capability',
	},
	{
		name: FeatureFlagName.CheatSheetAdmin,
		label: 'Cheat Sheet',
		description: 'Edit the Exterior Improvement process cheat sheet shown to staff',
		group: 'capability',
	},
	{
		name: FeatureFlagName.UserAdmin,
		label: 'User Admin',
		// Deliberately spells out the boundary: the capability this REPLACES was unbounded.
		description: 'Create, edit, remove and sign in as users, edit or delete their tasks, and '
			+ 'delete their location check-ins. Grants nothing else — no accounting, no '
			+ 'association or work-order rights.',
		group: 'capability',
	},
	{
		name: FeatureFlagName.AssociationAdmin,
		label: 'Associations',
		// Spells out the boundary, like User Admin: archiving is in, hard delete is not.
		description: 'Create, edit and archive associations, link them to their document folders, '
			+ 'and delete inspection records. Does NOT grant permanently deleting an association, '
			+ 'and grants nothing outside associations.',
		group: 'capability',
	},
	{
		name: FeatureFlagName.WorkOrderAdmin,
		label: 'Work Orders',
		// Spells out the boundary, like the others: two things beyond delete, and nothing more.
		description: 'Delete work orders, their time, material and misc-cost lines, and items in '
			+ 'the materials catalog, and correct a work order\'s Date of Order. Creating and '
			+ 'editing work orders already comes with the role, and this grants nothing else.',
		group: 'capability',
	},
	{
		name: FeatureFlagName.AccountingAdmin,
		label: 'Accounting',
		// Names the prerequisite, because on its own this flag does nothing: every
		// accounting endpoint checks the Accounting AREA flag before any permission.
		description: 'Edit accounting data — accounts, owners, dues, late fees, charges, '
			+ 'payments and credits — the same writes the Accounting role has. Requires the '
			+ 'Accounting flag as well, and grants nothing outside accounting.',
		group: 'capability',
	},
];

/**
 * The capability flags, by name — the group that means "an admin-only power on a non-admin".
 *
 * Derived, never hand-listed: the write paths that refuse a contractor a capability read this,
 * so a new `group: 'capability'` entry is covered the moment it is added. A hand-maintained
 * copy is how the FIRST version of that rule ended up naming only `user_admin`.
 */
export const CAPABILITY_FLAG_NAMES: ReadonlyArray<FeatureFlagName> = FEATURE_FLAGS
	.filter((f) => f.group === 'capability')
	.map((f) => f.name);

/** Whether granting this flag hands someone an admin-only power (vs. opening an area). */
export const isCapabilityFlag = (name: string): boolean =>
	(CAPABILITY_FLAG_NAMES as readonly string[]).includes(name);

/** Human label for a flag name, for error messages that a non-engineer has to act on. */
export const featureFlagLabel = (name: string): string =>
	FEATURE_FLAGS.find((f) => f.name === name)?.label ?? name;

export const featureFlagsInGroup = (group: FeatureFlagGroup): FeatureFlagDef[] =>
	FEATURE_FLAGS.filter((f) => f.group === group);

/**
 * Registry completeness check — every `FeatureFlagName` needs a display entry, or the flag
 * exists but no UI can toggle it. Asserted in tests/api/feature-flags.spec.ts.
 *
 * NOT the API's name validator: that uses `FeatureFlagNameSchema`, derived straight from the
 * enum, so it cannot drift. Keying the API on this hand-maintained array instead would turn a
 * forgotten display entry into an unsettable flag (hard 400) rather than a missing toggle.
 */
export const isRegisteredFeatureFlag = (name: string): boolean =>
	FEATURE_FLAGS.some((f) => f.name === name);
